import { throttle } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import IconButton from '../IconButton/IconButton';
import { WacomModes } from './Wacom.parts';
import { Handle, Line, Row, Tablet, Text, Wrapper } from './Wacom.style';
import { normalize, normalizeIn } from './Wacom.utils';

export type Mode = 'taper' | 'tilt' | 'slant' | 'scale';

type Position = { x: number; y: number };

export type WacomProps = {
  value: Position;
  mode: Mode;
  onDrag: (x: number, y: number) => void;
  onChange: (x: number, y: number) => void;
  onModeChange: (mode: Mode) => void;
  onReset: () => void;
};

export function Wacom(props: WacomProps) {
  const { mode, value } = props;
  const ref = useRef<HTMLDivElement>(null);
  const refDraggable = useRef<HTMLDivElement>(null);
  const [controlledPosition, setControlledPosition] = useState(normalizeIn(value));

  const onDragThrottled = throttle(props.onDrag, 100);

  useEffect(() => {
    const inPixels = normalizeIn(value);
    setControlledPosition(inPixels);
  }, [mode, value]);

  function onDrag(_ev: any, data: any) {
    const { x, y } = normalize(data);
    onDragThrottled(x, y);
  }

  function onClick(ev: any) {
    if (!ref.current) return;
    if (ev.target.className.includes('header')) {
      return;
    }

    const { left, top } = ref.current.getBoundingClientRect();
    const rx = ev.clientX - left;
    const ry = ev.clientY - top;
    const { x, y } = normalize({ x: rx, y: ry });
    props.onChange(x, y);
  }

  function onDragStop(_ev: any, data: any) {
    setControlledPosition({ x: data.x, y: data.y });
    const { x, y } = normalize({ x: data.x, y: data.y });
    props.onChange(x, y);
  }

  return (
    <Wrapper className='Wacom-wrapper' data-testid='Wacom-wrapper'>
      <Row>
        <WacomModes mode={mode} onModeChange={props.onModeChange} />
        <IconButton size={23} color='#ffffff44' name='IconRotate' onClick={props.onReset} />
      </Row>
      <Tablet ref={ref} onClick={onClick}>
        <Text>Wack</Text>
        <Line className='horizontal' />
        <Line className='vertical' />
        <Draggable
          bounds='parent'
          nodeRef={refDraggable}
          position={controlledPosition}
          onDrag={onDrag}
          onStop={onDragStop}
        >
          <Handle ref={refDraggable} />
        </Draggable>
      </Tablet>
    </Wrapper>
  );
}

export default Wacom;
